import {Interval} from "luxon";
import {singleton} from "tsyringe";

/**
 * Interface for reservation tokens returned by reserveAndFindGaps
 */
export interface ReservationToken {
    id: string;
    intervals: Interval[];
    timestamp: number;
}

@singleton()
export class IntervalLoadingGuard {

    private _intervals: Interval[] = [];
    private _pendingReservations: Map<string, ReservationToken> = new Map();

    public addInterval(interval: Interval): void {
        this._intervals = Interval.merge([...this._intervals, interval]);
    }

    public resetIntervals(): void {
        this._intervals = [];
    }

    public removeRange(cutInterval: Interval): void {
        const result: Interval[] = [];

        for (const existingInterval of this._intervals) {
            if (existingInterval.overlaps(cutInterval)) {
                if (cutInterval.engulfs(existingInterval)) {
                    // Wenn B das Intervall komplett umfasst, fügen wir es nicht zum Ergebnis hinzu
                } else if (existingInterval.engulfs(cutInterval)) {
                    // Wenn das Intervall B komplett enthält, teilen wir es auf
                    const beforeCutInterval = Interval.fromDateTimes(existingInterval.start!, cutInterval.start!);
                    const afterCutInterval = Interval.fromDateTimes(cutInterval.end!, existingInterval.end!);
                    if (!beforeCutInterval.isEmpty()) {
                        result.push(beforeCutInterval);
                    }
                    if (!afterCutInterval.isEmpty()) {
                        result.push(afterCutInterval);
                    }
                } else {
                    // Teilweise Überdeckung: Kürzen des Intervalls
                    const modifiedInterval = cutInterval.start! > existingInterval.start!
                        ? Interval.fromDateTimes(existingInterval.start!, cutInterval.start!)
                        : Interval.fromDateTimes(cutInterval.end!, existingInterval.end!);
                    if (!modifiedInterval.isEmpty()) {
                        result.push(modifiedInterval);
                    }
                }
            } else {
                // Keine Überlappung: Fügen Sie das unveränderte Intervall hinzu
                result.push(existingInterval);
            }
        }

        this._intervals = Interval.merge(result);
    }

    public findGapsInInterval(searchInterval: Interval): Interval[] {
        const sortedIntervals = [...this._intervals].sort((intervalA, intervalB) => intervalA.start!.toMillis() - intervalB.start!.toMillis());
        const gaps: Interval[] = [];

        let lastEnd = searchInterval.start;
        if (!lastEnd) {
            console.error(`There is a interval without given start datetime.`);
            return [];
        }

        for (const interval of sortedIntervals) {
            const start = interval.start;
            if (!start) {
                console.error(`One of the given intervals has no valid start datetime.`);
                continue;
            }

            if (start > lastEnd) {
                gaps.push(Interval.fromDateTimes(lastEnd, interval.start));
            }

            const end = interval.end;
            if (!end) {
                console.error(`One of the given intervals has no valid start datetime.`);
                return gaps;
            }

            if (interval.end > lastEnd) {
                lastEnd = interval.end;
            }
        }

        const end = searchInterval.end;
        if (!end) {
            console.error(`One of the given intervals has no valid start datetime.`);
            return [];
        }

        if (lastEnd < end) {
            gaps.push(Interval.fromDateTimes(lastEnd, end));
        }

        return gaps;
    }

    /**
     * Atomically reserve intervals and find gaps, preventing race conditions.
     * Returns a token for completing or cancelling the reservation.
     * 
     * @param searchInterval Interval to search for gaps
     * @returns Reservation token with gaps to load
     */
    public reserveAndFindGaps(searchInterval: Interval): ReservationToken {
        // Generate unique reservation ID
        const reservationId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Find gaps considering both loaded intervals and pending reservations
        const allReservedIntervals = [
            ...this._intervals,
            ...Array.from(this._pendingReservations.values()).flatMap(token => token.intervals)
        ];
        
        const gaps = this._findGapsInIntervalWithReserved(searchInterval, allReservedIntervals);
        
        // Create reservation token
        const token: ReservationToken = {
            id: reservationId,
            intervals: gaps,
            timestamp: Date.now()
        };
        
        // Store reservation
        this._pendingReservations.set(reservationId, token);
        
        return token;
    }

    /**
     * Complete a reservation by moving intervals from pending to loaded state
     * 
     * @param token Reservation token from reserveAndFindGaps
     */
    public completeReservation(token: ReservationToken): void {
        if (!this._pendingReservations.has(token.id)) {
            console.warn(`IntervalLoadingGuard: Reservation ${token.id} not found or already completed`);
            return;
        }
        
        // Move intervals from pending to loaded
        for (const interval of token.intervals) {
            this.addInterval(interval);
        }
        
        // Remove reservation
        this._pendingReservations.delete(token.id);
    }

    /**
     * Cancel a reservation (e.g., when request fails or is cancelled)
     * 
     * @param token Reservation token from reserveAndFindGaps
     */
    public cancelReservation(token: ReservationToken): void {
        this._pendingReservations.delete(token.id);
    }

    /**
     * Check if any part of the interval is currently pending or loaded
     * 
     * @param interval Interval to check
     * @returns True if interval is covered by loaded or pending intervals
     */
    public isIntervalCovered(interval: Interval): boolean {
        const allIntervals = [
            ...this._intervals,
            ...Array.from(this._pendingReservations.values()).flatMap(token => token.intervals)
        ];
        
        const gaps = this._findGapsInIntervalWithReserved(interval, allIntervals);
        return gaps.length === 0;
    }

    /**
     * Get current pending reservations (for debugging)
     */
    public getPendingReservations(): ReservationToken[] {
        return Array.from(this._pendingReservations.values());
    }

    /**
     * Clean up old reservations (older than 5 minutes)
     */
    public cleanupOldReservations(): void {
        const now = Date.now();
        const maxAge = 5 * 60 * 1000; // 5 minutes
        
        for (const [id, token] of this._pendingReservations.entries()) {
            if (now - token.timestamp > maxAge) {
                this._pendingReservations.delete(id);
            }
        }
    }

    /**
     * Find gaps in interval considering both loaded and reserved intervals
     */
    private _findGapsInIntervalWithReserved(searchInterval: Interval, allIntervals: Interval[]): Interval[] {
        const sortedIntervals = [...allIntervals].sort((intervalA, intervalB) => intervalA.start!.toMillis() - intervalB.start!.toMillis());
        const gaps: Interval[] = [];

        let lastEnd = searchInterval.start;
        if (!lastEnd) {
            console.error(`There is a interval without given start datetime.`);
            return [];
        }

        for (const interval of sortedIntervals) {
            const start = interval.start;
            if (!start) {
                console.error(`One of the given intervals has no valid start datetime.`);
                continue;
            }

            if (start > lastEnd) {
                gaps.push(Interval.fromDateTimes(lastEnd, interval.start));
            }

            const end = interval.end;
            if (!end) {
                console.error(`One of the given intervals has no valid start datetime.`);
                return gaps;
            }

            if (interval.end > lastEnd) {
                lastEnd = interval.end;
            }
        }

        const end = searchInterval.end;
        if (!end) {
            console.error(`One of the given intervals has no valid start datetime.`);
            return [];
        }

        if (lastEnd < end) {
            gaps.push(Interval.fromDateTimes(lastEnd, end));
        }

        return gaps;
    }
}